import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Product } from '@/types/product';

interface UseProductsOptions {
  enabled?: boolean;
  categoryId?: string;
  search?: string;
  inStock?: boolean;
}

export function useProducts(options: UseProductsOptions = {}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { enabled = true, categoryId, search, inStock } = options;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    loadProducts();
  }, [enabled, categoryId, search, inStock]);

  const loadProducts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get current user's company
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      // profiles.id IS the user_id (references auth.users.id)
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile) {
        throw new Error('User profile not found');
      }

      // Build query for products
      let query = supabase
        .from('products')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('is_active', true)
        .order('name');

      // Apply filters
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      if (inStock) {
        query = query.gt('stock', 0);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      // Transform to Product interface
      const transformedProducts: Product[] = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price,
        stock: item.stock,
        min_stock: item.min_stock,
        category_id: item.category_id?.toString(),
        cost_center_id: '1', // Default, can be enhanced
        is_active: item.is_active,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setProducts(transformedProducts);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err instanceof Error ? err : new Error('Failed to load products'));
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = () => loadProducts();

  return {
    products,
    isLoading,
    error,
    refetch,
  };
}